/**
 * Утилита для записи аудио с микрофона
 */

export class AudioRecorder {
  constructor() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.stream = null;
    this.startTime = null;
    this.maxDuration = 60; // seconds
    this.stopTimer = null;
  }

  /**
   * Определить поддерживаемый MIME тип
   */
  getSupportedMimeType() {
    const types = [
      'audio/mp4',
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        console.log('[AudioRecorder] Используется MIME тип:', type);
        return type;
      }
    }

    throw new Error('Браузер не поддерживает запись аудио');
  }

  /**
   * Начать запись
   */
  async startRecording() {
    try {
      // Запрашиваем доступ к микрофону
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });

      const mimeType = this.getSupportedMimeType();
      
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: mimeType
      });

      this.audioChunks = [];
      this.startTime = Date.now();

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
          console.log('[AudioRecorder] Получен фрагмент аудио:', event.data.size, 'байт');
        }
      };

      // ВАЖНО: timeslice=1000 - собирать данные каждую секунду
      // Без этого параметра браузер может не отправлять данные в ondataavailable
      this.mediaRecorder.start(1000);
      // console.log('[AudioRecorder] Запись началась (timeslice: 1000ms)');

      // Примечание: Автоостановку обрабатывает компонент (SimpleVoiceRecorder/VoiceNoteRecorder)
      // Не добавляем setTimeout здесь, чтобы избежать конфликтов!

      return mimeType;

    } catch (error) {
      console.error('[AudioRecorder] Ошибка начала записи:', error);
      throw error;
    }
  }

  /**
   * Остановить запись
   */
  async stopRecording() {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error('MediaRecorder не инициализирован'));
        return;
      }

      if (this.stopTimer) {
        clearTimeout(this.stopTimer);
      }

      this.mediaRecorder.onstop = () => {
        const duration = Math.floor((Date.now() - this.startTime) / 1000);
        const mimeType = this.mediaRecorder.mimeType;
        
        // console.log('[AudioRecorder] Запись остановлена');
        // console.log('[AudioRecorder] Собрано фрагментов:', this.audioChunks.length);
        // console.log('[AudioRecorder] Длительность:', duration, 'секунд');
        
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });
        
        // console.log('[AudioRecorder] Размер:', audioBlob.size, 'байт');
        // console.log('[AudioRecorder] MIME:', mimeType);

        // Останавливаем все треки
        if (this.stream) {
          this.stream.getTracks().forEach(track => track.stop());
        }

        resolve({
          blob: audioBlob,
          duration: duration,
          mimeType: mimeType,
          size: audioBlob.size
        });

        // Очистка
        this.mediaRecorder = null;
        this.audioChunks = [];
        this.stream = null;
      };

      this.mediaRecorder.stop();
    });
  }

  /**
   * Получить текущую длительность записи
   */
  getCurrentDuration() {
    if (!this.startTime) return 0;
    return Math.floor((Date.now() - this.startTime) / 1000);
  }

  /**
   * Проверить состояние записи
   */
  isRecording() {
    return this.mediaRecorder && this.mediaRecorder.state === 'recording';
  }

  /**
   * Отменить запись
   */
  cancelRecording() {
    if (this.mediaRecorder) {
      if (this.stopTimer) {
        clearTimeout(this.stopTimer);
      }
      
      this.mediaRecorder.stop();
      
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
      }

      this.mediaRecorder = null;
      this.audioChunks = [];
      this.stream = null;
    }
  }
}
